<template>
	<div
		class="grid min-h-9 w-full grid-cols-[2.5fr_4.5fr] items-center gap-x-1 rounded-md px-2 py-1 text-xs"
		:class="theme.background"
	>
		<div
			class="min-w-0"
			:class="theme.vendorText"
		>
			<slot></slot>
		</div>
		<div class="flex items-center justify-between gap-2">
			<div :class="theme.statusText">
				{{ toTitleCase(statusText ?? t('home.submissions.noneSent')) }}
			</div>
			<div class="flex items-center gap-2">
				<slot
					name="actions"
					:theme="theme"
				></slot>
				<InfoHover v-if="toolTipText">
					<div>
						<div class="flex justify-between">
							<div class="font-bold">
								{{ toTitleCase(toolTipText.title) }}
							</div>
							<div class="text-xs font-extralight">
								{{ formatLastUpdatedForSubmissionStatus(timeStamp) }}
							</div>
						</div>
						{{ toolTipText.body }}
					</div>
				</InfoHover>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { StatusThemeVariant } from '@/classes/SubmissionStatuses/StatusThemeVariantEnum'
import {
	formatLastUpdatedForSubmissionStatus,
	toTitleCase,
} from '@/classes/SubmissionStatuses/statusUtils'
import InfoHover from '@/components/presentation/InfoHoverV2.vue'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

export type SubmissionStatusLineProp = {
	variant?: StatusThemeVariant
	statusText?: string
	timeStamp?: string
	actions?: any[]
	toolTipText?: { title: string; body: string }
}

const { t } = useI18n()
const props = defineProps<SubmissionStatusLineProp>()

const themes: Record<
	StatusThemeVariant,
	{
		background: string
		vendorText: string
		statusText: string
		buttonBackground: string
		buttonBorder: string
		buttonText: string
	}
> = {
	success: {
		background: 'bg-accent-100',
		vendorText: 'text-slate-900 font-medium',
		statusText: 'text-accent-700',
		buttonBackground: 'bg-accent-400',
		buttonBorder: 'border-accent-500',
		buttonText: 'text-accent-800 font-medium',
	},
	pending: {
		background: 'bg-yellow-100',
		vendorText: 'text-slate-900 font-medium',
		statusText: 'text-yellow-700',
		buttonBackground: 'bg-accent-400',
		buttonBorder: 'border-accent-500',
		buttonText: 'text-accent-800 font-medium',
	},
	error: {
		background: 'bg-red-100',
		vendorText: 'text-slate-900 font-medium',
		statusText: 'text-red-800',
		buttonBackground: 'bg-amber-200',
		buttonBorder: 'border-amber-600',
		buttonText: 'text-amber-700',
	},
	neutral: {
		background: 'bg-white',
		vendorText: 'text-slate-600 font-medium',
		statusText: 'text-slate-600',
		buttonBackground: 'bg-amber-200',
		buttonBorder: 'border-amber-600',
		buttonText: 'text-amber-700',
	},
	inactive: {
		background: 'bg-white',
		vendorText: 'text-slate-400 font-medium',
		statusText: 'text-slate-400',
		buttonBackground: 'bg-slate-100',
		buttonBorder: 'border-slate-300',
		buttonText: 'text-slate-700',
	},
}

const theme = computed(() => {
	const variant = props.variant ?? StatusThemeVariant.INACTIVE
	return themes[variant]
})
</script>

<style scoped></style>
